// Declarative onboarding wizard definition.
// Universal questions run for every org. After Step 1 (Org Type) the
// matching branch's questions are appended.

export type FieldType = "select" | "state" | "number" | "percent" | "currency" | "text" | "yesno";

export interface OnboardingField {
  key: string;                    // path inside answers / branch_answers
  label: string;
  helper?: string;
  type: FieldType;
  options?: string[];             // for select
  required?: boolean;
  defaultBy?: Record<string, number>; // suggested default keyed by org_type
  defaultValue?: number;
  showIf?: (answers: Record<string, any>) => boolean;
  branchSection?: boolean;        // if true, value lives under branch_answers
}

export interface OnboardingStep {
  id: string;
  field: OnboardingField;
  acknowledgment?: (answers: Record<string, any>) => string | null;
}

export const ORG_TYPES = [
  "Community College",
  "4-Year Public University",
  "4-Year Private University",
  "K-12 School or District",
  "Nonprofit",
  "Corporate",
  "Other",
] as const;

export type OrgType = (typeof ORG_TYPES)[number];

export const PRIMARY_GOALS = [
  "Enrollment / Customer Growth",
  "Brand Awareness",
  "Operational Efficiency",
  "Executive Reporting",
  "Donor / Stakeholder Relations",
  "Other",
] as const;

const HIGHER_ED: OrgType[] = [
  "Community College",
  "4-Year Public University",
  "4-Year Private University",
];

export const isHigherEd = (t?: string | null) =>
  !!t && (HIGHER_ED as string[]).includes(t);

// Suggested annual salary defaults (rough national averages by org type)
export const SUGGESTED_ANNUAL_SALARY: Record<string, number> = {
  "Community College": 70000,
  "4-Year Public University": 85000,
  "4-Year Private University": 95000,
  "K-12 School or District": 65000,
  "Nonprofit": 60000,
  "Corporate": 110000,
  "Other": 75000,
};

// ---------- Universal Steps ----------
export const UNIVERSAL_STEPS: OnboardingStep[] = [
  {
    id: "org_type",
    field: {
      key: "org_type",
      label: "What best describes your organization?",
      type: "select",
      options: [...ORG_TYPES],
      required: true,
    },
  },
  {
    id: "org_state",
    field: {
      key: "org_state",
      label: "What state are you located in?",
      type: "state",
      required: true,
    },
    acknowledgment: (a) => {
      const t = a.org_type;
      if (isHigherEd(t)) return "Got it. A few questions specific to higher education.";
      if (t === "K-12 School or District") return "Got it. A few questions about your district.";
      if (t === "Nonprofit") return "Got it. A few questions about your organization.";
      if (t === "Corporate") return "Got it. A few questions about your business.";
      if (t === "Other") return "Got it. A few quick questions.";
      return null;
    },
  },
  {
    id: "org_scale",
    field: {
      key: "org_scale",
      label: "How many full-time staff members are on the team using Upbrief?",
      type: "number",
      required: true,
    },
  },
  {
    id: "avg_annual_salary",
    field: {
      key: "avg_annual_salary",
      label: "What is the average annual salary of a team member on your Upbrief team?",
      helper:
        "We'll use this to calculate hourly value automatically — no need to enter an hourly rate separately.",
      type: "currency",
      required: true,
      defaultBy: SUGGESTED_ANNUAL_SALARY,
    },
  },
  {
    id: "annual_budget",
    field: {
      key: "annual_budget",
      label: "What is your team's approximate annual operating budget?",
      type: "currency",
      required: false,
    },
  },
  {
    id: "primary_goal",
    field: {
      key: "primary_goal",
      label: "What is your team's primary reason for using Upbrief?",
      type: "select",
      options: [...PRIMARY_GOALS],
      required: true,
    },
  },
];

// ---------- Branch Steps ----------
const HIGHER_ED_STEPS: OnboardingStep[] = [
  {
    id: "tuition_per_credit_hour",
    field: {
      key: "tuition_per_credit_hour",
      label: "What is your current tuition per credit hour?",
      type: "currency",
      required: true,
      branchSection: true,
    },
  },
  {
    id: "credits_per_semester",
    field: {
      key: "credits_per_semester",
      label: "What is the average number of credits a student takes per semester?",
      type: "number",
      required: true,
      branchSection: true,
      defaultBy: {
        "Community College": 9,
        "4-Year Public University": 15,
        "4-Year Private University": 15,
      },
    },
  },
  {
    id: "credits_to_complete",
    field: {
      key: "credits_to_complete",
      label: "What is the average number of credits a student needs to complete their program?",
      type: "number",
      required: true,
      branchSection: true,
      defaultBy: {
        "Community College": 60,
        "4-Year Public University": 120,
        "4-Year Private University": 120,
      },
    },
  },
  {
    id: "retention_rate",
    field: {
      key: "retention_rate",
      label: "What is your institution's current student retention rate?",
      helper: "Enter as a percentage (0–100).",
      type: "percent",
      required: true,
      branchSection: true,
      defaultBy: {
        "Community College": 55,
        "4-Year Public University": 75,
        "4-Year Private University": 75,
      },
    },
  },
  {
    id: "has_fte_funding",
    field: {
      key: "has_fte_funding",
      label: "Does your institution receive state funding per enrolled student (FTE funding)?",
      type: "yesno",
      required: true,
      branchSection: true,
    },
  },
  {
    id: "fte_funding_amount",
    field: {
      key: "fte_funding_amount",
      label: "What is the approximate annual state funding amount per FTE student?",
      type: "currency",
      required: false,
      branchSection: true,
      showIf: (a) => a.branch_answers?.has_fte_funding === true,
    },
  },
  {
    id: "new_enrollment_last_year",
    field: {
      key: "new_enrollment_last_year",
      label: "How many new students did you enroll last year?",
      type: "number",
      required: false,
      branchSection: true,
    },
  },
];

const K12_STEPS: OnboardingStep[] = [
  {
    id: "per_pupil_expenditure",
    field: {
      key: "per_pupil_expenditure",
      label: "What is your per-pupil expenditure?",
      helper: "This is typically available in your district's annual budget report.",
      type: "currency",
      required: true,
      branchSection: true,
    },
  },
  {
    id: "current_enrollment",
    field: {
      key: "current_enrollment",
      label: "What is your current student enrollment?",
      type: "number",
      required: true,
      branchSection: true,
    },
  },
  {
    id: "has_extra_funding",
    field: {
      key: "has_extra_funding",
      label:
        "Does your district receive additional state or federal funding per student beyond base per-pupil expenditure?",
      type: "yesno",
      required: true,
      branchSection: true,
    },
  },
  {
    id: "extra_funding_amount",
    field: {
      key: "extra_funding_amount",
      label: "Approximate additional funding per student?",
      type: "currency",
      required: false,
      branchSection: true,
      showIf: (a) => a.branch_answers?.has_extra_funding === true,
    },
  },
];

const NONPROFIT_STEPS: OnboardingStep[] = [
  {
    id: "avg_gift_size",
    field: {
      key: "avg_gift_size",
      label: "What is your average donation or gift size?",
      type: "currency",
      required: false,
      branchSection: true,
    },
  },
  {
    id: "donor_retention_rate",
    field: {
      key: "donor_retention_rate",
      label: "What is your approximate donor or supporter retention rate?",
      type: "percent",
      required: false,
      branchSection: true,
    },
  },
  {
    id: "uses_volunteers",
    field: {
      key: "uses_volunteers",
      label: "Do volunteers contribute to your team's work?",
      type: "yesno",
      required: true,
      branchSection: true,
    },
  },
  {
    id: "volunteer_hour_value",
    field: {
      key: "volunteer_hour_value",
      label: "What would you estimate is the value of one volunteer hour to your organization?",
      helper: "National average is about $29/hr.",
      type: "currency",
      required: false,
      branchSection: true,
      defaultValue: 29,
      showIf: (a) => a.branch_answers?.uses_volunteers === true,
    },
  },
  {
    id: "avg_grant_size",
    field: {
      key: "avg_grant_size",
      label: "What is the average size of grants your organization receives?",
      type: "currency",
      required: false,
      branchSection: true,
    },
  },
];

const CORPORATE_STEPS: OnboardingStep[] = [
  {
    id: "avg_contract_value",
    field: {
      key: "avg_contract_value",
      label: "What is your average contract or deal value?",
      type: "currency",
      required: false,
      branchSection: true,
    },
  },
  {
    id: "customer_lifetime_value",
    field: {
      key: "customer_lifetime_value",
      label: "What is the average lifetime value of one customer?",
      type: "currency",
      required: false,
      branchSection: true,
    },
  },
  {
    id: "cost_per_lead",
    field: {
      key: "cost_per_lead",
      label: "What is your approximate cost per lead?",
      type: "currency",
      required: false,
      branchSection: true,
    },
  },
  {
    id: "new_customers_last_year",
    field: {
      key: "new_customers_last_year",
      label: "How many new customers did you acquire last year?",
      type: "number",
      required: false,
      branchSection: true,
    },
  },
];

const OTHER_STEPS: OnboardingStep[] = [
  {
    id: "value_description",
    field: {
      key: "value_description",
      label: "How would you describe the primary value your team delivers?",
      type: "text",
      required: true,
      branchSection: true,
    },
  },
  {
    id: "new_customer_value",
    field: {
      key: "new_customer_value",
      label:
        "What does acquiring one new customer or stakeholder mean financially to your organization?",
      type: "currency",
      required: false,
      branchSection: true,
    },
  },
];

export function getBranchSteps(orgType: string | undefined | null): OnboardingStep[] {
  if (!orgType) return [];
  if (isHigherEd(orgType)) return HIGHER_ED_STEPS;
  if (orgType === "K-12 School or District") return K12_STEPS;
  if (orgType === "Nonprofit") return NONPROFIT_STEPS;
  if (orgType === "Corporate") return CORPORATE_STEPS;
  if (orgType === "Other") return OTHER_STEPS;
  return [];
}

export function getAllSteps(answers: Record<string, any>): OnboardingStep[] {
  const branch = getBranchSteps(answers.org_type);
  const all = [...UNIVERSAL_STEPS, ...branch];
  return all.filter((s) => !s.field.showIf || s.field.showIf(answers));
}

// Hourly rate derivation (read-only display + ROI fallback)
// US standard: 2080 working hours per year (40h * 52w)
export const ANNUAL_HOURS = 2080;
export const deriveHourlyRate = (annualSalary?: number | null): number | null =>
  annualSalary && annualSalary > 0 ? Math.round((annualSalary / ANNUAL_HOURS) * 100) / 100 : null;

// US States
export const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];
